// Delegated handlers for controls that appear on many screens: bookmarks,
// notes, key-term sheets and read-aloud. Views only render the buttons.
import { html, paragraphs } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { actions, noteFor, isBookmarked } from '../lib/store.js';
import { concept, person, cachedChapter, loadVerse } from '../lib/content.js';
import { speak, stop, speaking } from '../lib/speech.js';
import { navigate } from '../lib/router.js';
import { openSheet } from './sheet.js';
import { toast } from './toast.js';

function refreshBookmarkButtons(id) {
  const on = isBookmarked(id);
  for (const b of document.querySelectorAll(`[data-bookmark="${CSS.escape(id)}"]`)) {
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    b.innerHTML = String(icon(on ? 'bookmark-on' : 'bookmark', { size: 20 }));
  }
}

function refreshNoteUi(id) {
  const note = noteFor(id);
  for (const b of document.querySelectorAll(`button.icon-btn[data-note="${CSS.escape(id)}"]`)) {
    b.setAttribute('aria-pressed', note ? 'true' : 'false');
    b.setAttribute('aria-label', `${note ? 'Edit your note' : 'Add a note'} on verse ${id}`);
  }
  for (const article of document.querySelectorAll(`[data-verse="${CSS.escape(id)}"]`)) {
    let preview = article.querySelector('.verse__mynote');
    if (!note) {
      if (preview) preview.remove();
      continue;
    }
    if (!preview) {
      preview = document.createElement('button');
      preview.type = 'button';
      preview.className = 'verse__mynote';
      preview.dataset.note = id;
      article.appendChild(preview);
    }
    preview.innerHTML = String(html`<span class="eyebrow">Your note</span><span class="verse__mynote-text">${note.text}</span>`);
  }
  document.dispatchEvent(new CustomEvent('bg:note', { detail: { id } }));
}

export function openNoteEditor(key, { title = 'Your note', prompt = '' } = {}) {
  const existing = noteFor(key);
  const { el, close } = openSheet({
    title,
    body: html`
      ${prompt ? html`<p class="muted" style="margin-bottom:12px">${prompt}</p>` : ''}
      <label class="sr-only" for="note-text">Note</label>
      <textarea id="note-text" class="textarea" rows="7" maxlength="8000" placeholder="What stood out to you? What does it mean for your life?">${existing ? existing.text : ''}</textarea>
      <p class="field__hint" style="margin-top:8px">Notes are private. They are saved on this device, and synced if you are signed in.</p>`,
    foot: html`
      ${existing ? html`<button type="button" class="btn btn--danger" data-delete>Delete</button>` : html`<button type="button" class="btn btn--secondary" data-sheet-close>Cancel</button>`}
      <button type="button" class="btn btn--primary" data-save>Save note</button>`,
  });
  const ta = el.querySelector('textarea');
  setTimeout(() => ta.focus(), 50);
  el.querySelector('[data-save]').addEventListener('click', () => {
    actions.setNote(key, ta.value.trim());
    close();
    refreshNoteUi(key);
    toast(ta.value.trim() ? 'Note saved' : 'Note removed');
  });
  const del = el.querySelector('[data-delete]');
  if (del) {
    del.addEventListener('click', () => {
      actions.setNote(key, '');
      close();
      refreshNoteUi(key);
      toast('Note deleted');
    });
  }
}

export function openConceptSheet(id) {
  const g = concept(id);
  if (!g) return;
  const first = String(g.body).split(/\n\n+/)[0];
  const { el, close } = openSheet({
    title: g.term,
    body: html`
      <p class="concept-sheet__sa"><span class="deva" lang="sa">${g.sa}</span><span class="iast" lang="sa-Latn">${g.iast}</span></p>
      <p class="concept-sheet__short">${g.short}</p>
      <div class="prose muted">${paragraphs(first)}</div>`,
    foot: html`<a class="btn btn--secondary btn--block" href="/concept/${g.id}" data-open>Read more about ${g.term} ${icon('arrow-right', { size: 18 })}</a>`,
  });
  el.querySelector('[data-open]').addEventListener('click', (e) => {
    e.preventDefault();
    close();
    navigate(`/concept/${g.id}`);
  });
}

export function openPersonSheet(id) {
  const p = person(id);
  if (!p) return;
  openSheet({
    title: p.name,
    body: html`
      <p class="concept-sheet__sa"><span class="deva" lang="sa">${p.sa}</span><span class="muted">${p.role}</span></p>
      <p class="concept-sheet__short">${p.short}</p>
      <div class="prose muted">${paragraphs(String(p.body).split(/\n\n+/)[0])}</div>`,
  });
}

async function listen(button, id) {
  const pressed = button.getAttribute('aria-pressed') === 'true';
  for (const b of document.querySelectorAll('[data-listen][aria-pressed="true"]')) {
    b.setAttribute('aria-pressed', 'false');
    b.innerHTML = String(icon('listen', { size: 20 }));
  }
  if (pressed || speaking()) {
    stop();
    if (pressed) return;
  }
  const [ch, v] = id.split('.').map(Number);
  const data = cachedChapter(ch);
  const verse = data ? data.verseMap.get(v) : await loadVerse(id);
  if (!verse) return;
  button.setAttribute('aria-pressed', 'true');
  button.innerHTML = String(icon('pause', { size: 20 }));
  speak(`${verse.translation} ... ${verse.meaning}`, () => {
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML = String(icon('listen', { size: 20 }));
  });
}

export function bindGlobalActions() {
  document.addEventListener('click', (e) => {
    const bm = e.target.closest('[data-bookmark]');
    if (bm) {
      const id = bm.dataset.bookmark;
      const on = actions.toggleBookmark(id);
      refreshBookmarkButtons(id);
      toast(on ? `Verse ${id} bookmarked` : 'Bookmark removed', on ? { action: 'View', onAction: () => navigate('/saved') } : {});
      return;
    }
    const note = e.target.closest('[data-note]');
    if (note) {
      openNoteEditor(note.dataset.note, { title: `Note on ${note.dataset.note}` });
      return;
    }
    const term = e.target.closest('[data-term]');
    if (term) {
      openConceptSheet(term.dataset.term);
      return;
    }
    const l = e.target.closest('[data-listen]');
    if (l) listen(l, l.dataset.listen);
  });
  // Stop reading aloud when the page changes.
  window.addEventListener('popstate', () => stop());
  document.addEventListener('click', (e) => {
    if (e.target.closest('a[href^="/"]')) stop();
  }, true);
}
